export type PolicyBlock =
  | { kind: 'p'; text: string }
  | { kind: 'quote'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'defs'; items: { term: string; text: string }[] };

export type PolicySection = {
  id: string;
  num: string;
  title: string;
  blocks: PolicyBlock[];
};

export type WhistleblowerCopy = {
  title: string;
  description: string;
  badge: string;
  headline: string;
  lede: string;
  meta: { label: string; value: string }[];
  reportTitle: string;
  reportText: string;
  reportEmailLabel: string;
  reportEmail: string;
  financeNote: string;
  contactsTitle: string;
  auditRole: string;
  ethicsRole: string;
  contents: string;
  signatureName: string;
  signatureRole: string;
  sections: PolicySection[];
};

const address = 'TATA AutoComp Systems, TACO House, Damle Path, Off Law College Road, Pune 411004';

export const whistleblowerCopy: Record<'sk' | 'en', WhistleblowerCopy> = {
  en: {
    title: 'Whistle Blower Policy | Artifex Systems Slovakia',
    description:
      'Whistle Blower Policy HR/P28 of Tata AutoComp Systems. How employees and directors can report concerns to the Ethics Counsellor or the Chairperson of the Audit Committee.',
    badge: 'HR/P28 · Tata AutoComp Systems',
    headline: 'Whistle Blower Policy',
    lede:
      'A mechanism for directors and employees to report actual or possible violations of the Tata Code of Conduct, or events that could affect the business or reputation of the company or any other Tata company.',
    meta: [
      { label: 'Document', value: 'HR/P28' },
      { label: 'In force from', value: '8 May 2006' },
      { label: 'Revision', value: '006 · 21 April 2025' },
      { label: 'Approved by', value: 'Sudipta Marjit' },
    ],
    reportTitle: 'How to report',
    reportText:
      'Address a protected disclosure to the Ethics Counsellor or to the Chairperson of the Audit Committee. You can also send it by email.',
    reportEmailLabel: 'Write to',
    reportEmail: 'whistle.blower@tataautocomp.com',
    financeNote:
      'Protected disclosures on financial or accounting matters go to the Chairperson of the Audit Committee.',
    contactsTitle: 'Ethics Counsellor and Audit Committee',
    auditRole: 'Chairperson of the Audit Committee',
    ethicsRole: 'Chief Ethics Counsellor',
    contents: 'Contents',
    signatureName: 'Sudipta Marjit',
    signatureRole: 'CHRO and Chief Ethics Counsellor, Tata AutoComp Systems',
    sections: [
      {
        id: 'preface',
        num: '01',
        title: 'Preface',
        blocks: [
          {
            kind: 'p',
            text: 'The Company believes in the conduct of the affairs of its constituents in a fair and transparent manner by adopting the highest standards of professionalism, honesty, integrity and ethical behaviour. Towards this end, the Company has adopted the Tata Code of Conduct (“the Code”), which lays down the principles and standards that should govern the actions of the Company and its employees. Any actual or potential violation of the Code, howsoever insignificant or perceived as such, would be a matter of serious concern for the Company. The role of employees in pointing out such violations of the Code cannot be undermined. There is a provision under the Code for employees to report violations, which states:',
          },
          {
            kind: 'quote',
            text: 'The Management encourages every employee of the company to promptly report any actual or possible violation of the code or an event he or she becomes aware of that could affect the business or reputation of the company or any other Tata company.',
          },
          {
            kind: 'p',
            text: 'Accordingly, this Whistle Blower Policy (“the Policy”) has been formulated to provide a mechanism for employees of the Company to approach the Ethics Counsellor or the Chairperson of the Audit Committee of the Company. The Policy is also the vigilance mechanism of the Company as envisaged under Section 177 of the Companies Act, 2013 and the rules framed thereunder.',
          },
        ],
      },
      {
        id: 'scope',
        num: '02',
        title: 'Scope',
        blocks: [
          {
            kind: 'p',
            text: 'This Policy is an extension of the Tata Code of Conduct. The Whistle Blower’s role is that of a reporting party with reliable information. They are not required or expected to act as investigators or finders of facts, nor would they determine the appropriate corrective or remedial action that may be warranted in a given case.',
          },
          {
            kind: 'p',
            text: 'Whistle Blowers should not act on their own in conducting any investigative activities, nor do they have a right to participate in any investigative activities other than as requested by the Ethics Counsellor, the Chairperson of the Audit Committee, or the Investigators.',
          },
          {
            kind: 'p',
            text: 'A Protected Disclosure will be dealt with by the Ethics Counsellor or the Chairperson of the Audit Committee, as the case may be.',
          },
        ],
      },
      {
        id: 'commencement',
        num: '03',
        title: 'Date of commencement',
        blocks: [
          {
            kind: 'p',
            text: 'This Policy came into operation with effect from 8 May 2006, on being adopted by the Board of Directors of the Company.',
          },
        ],
      },
      {
        id: 'eligibility',
        num: '04',
        title: 'Eligibility',
        blocks: [
          {
            kind: 'p',
            text: 'All Directors and Employees of the Company are eligible to make Protected Disclosures under the Policy. The Protected Disclosures may relate to matters concerning the Company or any other Tata company.',
          },
        ],
      },
      {
        id: 'disqualifications',
        num: '05',
        title: 'Disqualifications',
        blocks: [
          {
            kind: 'p',
            text: 'Genuine Whistle Blowers will be accorded complete protection from any kind of unfair treatment as set out in this Policy. Any abuse of this protection will warrant disciplinary action.',
          },
          {
            kind: 'p',
            text: 'Protection under this Policy does not mean protection from disciplinary action arising out of false or bogus allegations made by a Whistle Blower knowing them to be false or bogus, or made with a mala fide intention.',
          },
          {
            kind: 'p',
            text: 'Whistle Blowers who make three or more Protected Disclosures which are subsequently found to be mala fide, frivolous, baseless, malicious, or reported otherwise than in good faith, will be disqualified from reporting further Protected Disclosures under this Policy. In respect of such Whistle Blowers, the Company and the Audit Committee reserve the right to take or recommend appropriate disciplinary action.',
          },
        ],
      },
      {
        id: 'definitions',
        num: '06',
        title: 'Definitions',
        blocks: [
          {
            kind: 'p',
            text: 'Capitalised terms not defined here have the meaning assigned to them under the Code.',
          },
          {
            kind: 'defs',
            items: [
              {
                term: 'Audit Committee',
                text: 'The Audit Committee constituted by the Board of Directors of the Company in accordance with Section 177 of the Companies Act, 2013.',
              },
              {
                term: 'Employee',
                text: 'Every employee of the Company, irrespective of category.',
              },
              {
                term: 'Director',
                text: 'A member of the Board of the Company.',
              },
              {
                term: 'Code',
                text: 'The Tata Code of Conduct.',
              },
              {
                term: 'Investigators',
                text: 'Persons authorised, appointed, consulted or approached by the Ethics Counsellor or the Chairperson of the Audit Committee, including the auditors of the Company and the police.',
              },
              {
                term: 'Protected Disclosure',
                text: 'Any communication made in good faith that discloses or demonstrates information that may evidence unethical or improper activity.',
              },
              {
                term: 'Subject',
                text: 'A person against or in relation to whom a Protected Disclosure has been made, or evidence gathered during an investigation.',
              },
              {
                term: 'Whistle Blower',
                text: 'An Employee making a Protected Disclosure under this Policy.',
              },
            ],
          },
        ],
      },
      {
        id: 'procedure',
        num: '07',
        title: 'Procedure',
        blocks: [
          {
            kind: 'p',
            text: `All Protected Disclosures concerning any matter should be addressed to the Ethics Counsellor of the Company or to the Chairperson of the Audit Committee at the respective business unit for investigation. They may also be emailed to whistle.blower@tataautocomp.com.`,
          },
          {
            kind: 'p',
            text: 'All Protected Disclosures concerning financial or accounting matters should be addressed to the Chairperson of the Audit Committee of the Company for investigation.',
          },
          {
            kind: 'p',
            text: `Reach the Ethics Counsellor or the Chairperson of the Audit Committee as follows. Ms. Varsha Purandare, Chairperson of the Audit Committee, ${address}, varsha0712@outlook.com. Mr. Sudipta Marjit, Chief Ethics Counsellor, ${address}, Sudipta.marjit@tataautocomp.com.`,
          },
          {
            kind: 'list',
            items: [
              'If a Protected Disclosure is received by any executive of the Company other than the Chairperson of the Audit Committee or the Ethics Counsellor, it should be forwarded to the Company’s Ethics Counsellor and/or the Chairperson of the Audit Committee. Appropriate care must be taken to keep the identity of the Whistle Blower confidential.',
              'Protected Disclosures should preferably be reported in writing so that the issues raised are clearly understood. They should be typed or written in legible handwriting in English, Hindi, or the regional language of the Whistle Blower’s place of employment.',
              'The Protected Disclosure should be forwarded under a covering letter which bears the identity of the Whistle Blower. The Chairperson of the Audit Committee or the Ethics Counsellor, as the case may be, shall detach the covering letter and forward only the Protected Disclosure to the Investigators.',
              'Protected Disclosures should be factual and not speculative or conclusive. They should contain as much specific information as possible so that the nature and extent of the concern, and the urgency of a preliminary investigation, can be properly assessed.',
              'The Whistle Blower must disclose his or her identity in the covering letter. Anonymous disclosures may not be entertained, because it would not be possible to interview the Whistle Blower.',
            ],
          },
        ],
      },
      {
        id: 'investigation',
        num: '08',
        title: 'Investigation',
        blocks: [
          {
            kind: 'p',
            text: 'All Protected Disclosures reported under this Policy will be thoroughly investigated by the Ethics Counsellor or the Chairperson of the Audit Committee of the Company. They will investigate or oversee the investigations under the authorisation of the Audit Committee.',
          },
          {
            kind: 'p',
            text: 'The Ethics Counsellor or the Chairperson of the Audit Committee may, at their discretion, involve any Investigators for the purpose of the investigation.',
          },
          {
            kind: 'p',
            text: 'The decision to conduct an investigation is not itself an accusation. It is a neutral fact-finding process. The outcome may not support the Whistle Blower’s conclusion that an improper or unethical act was committed.',
          },
          {
            kind: 'p',
            text: 'The identity of a Subject will be kept confidential to the extent possible, given the legitimate needs of the law and the investigation. Subjects will normally be informed of the allegations at the outset of a formal investigation and will have opportunities to provide their inputs during the investigation.',
          },
          {
            kind: 'p',
            text: 'Subjects have a duty to co-operate with the Ethics Counsellor, the Chairperson of the Audit Committee, or any of the Investigators, to the extent that such co-operation will not compromise self-incrimination protections available under the applicable laws.',
          },
          {
            kind: 'p',
            text: 'Subjects have a right to consult with a person or persons of their choice, other than the Ethics Counsellor, the Investigators, members of the Audit Committee, or the Whistle Blower. Subjects are free at any time to engage counsel at their own cost to represent them in the investigation proceedings.',
          },
          {
            kind: 'p',
            text: 'Subjects have a responsibility not to interfere with the investigation. Evidence shall not be withheld, destroyed or tampered with, and witnesses shall not be influenced, coached, threatened or intimidated by the Subjects.',
          },
          {
            kind: 'p',
            text: 'Unless there are compelling reasons not to do so, Subjects will be given the opportunity to respond to material findings in an investigation report. No allegation of wrongdoing against a Subject shall be considered maintainable unless there is good evidence in support of the allegation.',
          },
          {
            kind: 'p',
            text: 'Subjects have a right to be informed of the outcome of the investigation. If allegations are not sustained, the Subject should be consulted as to whether public disclosure of the investigation results would be in the best interest of the Subject and the Company.',
          },
          {
            kind: 'p',
            text: 'The investigation shall normally be completed within 45 days of receipt of the Protected Disclosure.',
          },
        ],
      },
      {
        id: 'protection',
        num: '09',
        title: 'Protection',
        blocks: [
          {
            kind: 'p',
            text: 'No unfair treatment will be meted out to a Whistle Blower by virtue of his or her having reported a Protected Disclosure under this Policy. The Company, as a policy, condemns any kind of discrimination, harassment, victimisation or any other unfair employment practice against Whistle Blowers.',
          },
          {
            kind: 'p',
            text: 'Complete protection will therefore be given to Whistle Blowers against any unfair practice such as retaliation, threat or intimidation of termination or suspension of service, disciplinary action, transfer, demotion, refusal of promotion, or the like, including any direct or indirect use of authority to obstruct the Whistle Blower’s right to continue to perform his or her duties and functions, including making a further Protected Disclosure.',
          },
          {
            kind: 'p',
            text: 'Direct access to the Chairperson of the Audit Committee will be provided in appropriate and exceptional cases. The Company will take steps to minimise difficulties which the Whistle Blower may experience as a result of making the Protected Disclosure. If the Whistle Blower is required to give evidence in criminal or disciplinary proceedings, the Company will arrange for the Whistle Blower to receive advice about the procedure.',
          },
          {
            kind: 'p',
            text: 'A Whistle Blower may report any violation of the above to the Chairperson of the Audit Committee, who shall investigate it and recommend suitable action to the management.',
          },
          {
            kind: 'p',
            text: 'The identity of the Whistle Blower shall be kept confidential to the extent possible and permitted under law. Whistle Blowers are cautioned that their identity may become known for reasons outside the control of the Ethics Counsellor or the Chairperson of the Audit Committee, for example during investigations carried out by Investigators.',
          },
          {
            kind: 'p',
            text: 'Any other Employee assisting in the investigation shall also be protected to the same extent as the Whistle Blower.',
          },
        ],
      },
      {
        id: 'investigators',
        num: '10',
        title: 'Investigators',
        blocks: [
          {
            kind: 'p',
            text: 'Investigators are required to conduct a process of fact-finding and analysis. They derive their authority and access rights from the Ethics Counsellor or the Audit Committee when acting within the course and scope of their investigation.',
          },
          {
            kind: 'p',
            text: 'Technical and other resources may be drawn upon as necessary to augment the investigation. All Investigators shall be independent and unbiased, both in fact and as perceived. Investigators have a duty of fairness, objectivity, thoroughness, ethical behaviour, and observance of legal and professional standards.',
          },
          {
            kind: 'p',
            text: 'Investigations will be launched only after a preliminary review which establishes that:',
          },
          {
            kind: 'list',
            items: [
              'The alleged act constitutes an improper or unethical activity or conduct, and',
              'Either the allegation is supported by information specific enough to be investigated, or the matter does not meet this standard. Matters that do not meet this standard may be worthy of management review, but should not be undertaken as an investigation of an improper or unethical activity.',
            ],
          },
        ],
      },
      {
        id: 'decision',
        num: '11',
        title: 'Decision',
        blocks: [
          {
            kind: 'p',
            text: 'If an investigation leads the Ethics Counsellor or the Chairperson of the Audit Committee to conclude that an improper or unethical act has been committed, they shall recommend to the management of the Company such disciplinary or corrective action as they deem fit. Any disciplinary or corrective action initiated against the Subject as a result of the findings shall adhere to the applicable personnel or staff conduct and disciplinary procedures.',
          },
        ],
      },
      {
        id: 'reporting',
        num: '12',
        title: 'Reporting',
        blocks: [
          {
            kind: 'p',
            text: 'The Ethics Counsellor shall submit a report to the Audit Committee on a regular basis about all Protected Disclosures referred to him or her since the last report, together with the results of investigations, if any.',
          },
        ],
      },
      {
        id: 'retention',
        num: '13',
        title: 'Retention of documents',
        blocks: [
          {
            kind: 'p',
            text: 'All Protected Disclosures in writing or documented, together with the results of the investigation relating to them, shall be retained by the Company for a minimum period of seven years.',
          },
        ],
      },
      {
        id: 'amendment',
        num: '14',
        title: 'Power to amend',
        blocks: [
          {
            kind: 'p',
            text: 'The Company reserves the right to amend or modify this Policy in whole or in part, at any time, without assigning any reason whatsoever.',
          },
        ],
      },
    ],
  },
  sk: {
    title: 'Politika oznamovania nekalých praktík | Artifex Systems Slovakia',
    description:
      'Politika oznamovania nekalých praktík HR/P28 spoločnosti Tata AutoComp Systems. Ako môžu zamestnanci a riaditelia oznámiť podnet poradcovi pre etiku alebo predsedníčke Výboru pre audit.',
    badge: 'HR/P28 · Tata AutoComp Systems',
    headline: 'Politika oznamovania nekalých praktík',
    lede:
      'Mechanizmus, ktorým riaditelia a zamestnanci nahlasujú skutočné alebo možné porušenie Kódexu správania Tata, alebo udalosť, ktorá by mohla ovplyvniť podnikanie či dobré meno spoločnosti alebo inej spoločnosti Tata.',
    meta: [
      { label: 'Dokument', value: 'HR/P28' },
      { label: 'Účinnosť od', value: '8. mája 2006' },
      { label: 'Revízia', value: '006 · 21. apríla 2025' },
      { label: 'Schválil', value: 'Sudipta Marjit' },
    ],
    reportTitle: 'Ako podať oznámenie',
    reportText:
      'Chránené oznámenie adresujte poradcovi pre etiku alebo predsedníčke Výboru pre audit. Môžete ho poslať aj e-mailom.',
    reportEmailLabel: 'Napíšte na',
    reportEmail: 'whistle.blower@tataautocomp.com',
    financeNote:
      'Chránené oznámenia vo veciach financií alebo účtovníctva patria predsedníčke Výboru pre audit.',
    contactsTitle: 'Poradca pre etiku a Výbor pre audit',
    auditRole: 'Predsedníčka Výboru pre audit',
    ethicsRole: 'Hlavný etický poradca',
    contents: 'Obsah',
    signatureName: 'Sudipta Marjit',
    signatureRole: 'CHRO a hlavný etický poradca, Tata AutoComp Systems',
    sections: [
      {
        id: 'preface',
        num: '01',
        title: 'Predslov',
        blocks: [
          {
            kind: 'p',
            text: 'Spoločnosť verí v spravodlivé a transparentné vedenie záležitostí svojich členov prijatím najvyšších štandardov profesionality, čestnosti, integrity a etického správania. S týmto cieľom prijala Kódex správania spoločnosti Tata (ďalej len „Kódex“), ktorý stanovuje zásady a normy, ktorými sa má riadiť konanie Spoločnosti a jej zamestnancov. Akékoľvek skutočné alebo potenciálne porušenie Kódexu, aj keď sa javí ako bezvýznamné, je pre Spoločnosť predmetom vážneho záujmu. Úlohu zamestnancov pri poukazovaní na takéto porušenia nemožno podceňovať. Kódex obsahuje ustanovenie, podľa ktorého môžu zamestnanci nahlasovať porušenia:',
          },
          {
            kind: 'quote',
            text: 'Vedenie vyzýva každého zamestnanca spoločnosti, aby bezodkladne nahlásil akékoľvek skutočné alebo možné porušenie kódexu alebo udalosť, o ktorej sa dozvedel a ktorá by mohla ovplyvniť podnikanie alebo dobré meno spoločnosti alebo inej spoločnosti Tata.',
          },
          {
            kind: 'p',
            text: 'Preto bola táto Politika oznamovania nekalých praktík (ďalej len „Politika“) prijatá s cieľom poskytnúť zamestnancom Spoločnosti mechanizmus, ako sa obrátiť na poradcu pre etiku alebo predsedu Výboru pre audit Spoločnosti. Politika je zároveň mechanizmom vigilancie Spoločnosti podľa článku 177 zákona o spoločnostiach z roku 2013 a pravidiel vydaných na jeho základe.',
          },
        ],
      },
      {
        id: 'scope',
        num: '02',
        title: 'Rozsah pôsobnosti',
        blocks: [
          {
            kind: 'p',
            text: 'Táto Politika je rozšírením Kódexu správania spoločnosti Tata. Úlohou oznamovateľa je podať spoľahlivé informácie. Nevyžaduje sa od neho a neočakáva sa, že bude konať ako vyšetrovateľ alebo zisťovateľ skutkového stavu, ani že určí vhodné nápravné opatrenie.',
          },
          {
            kind: 'p',
            text: 'Oznamovatelia nemajú konať na vlastnú päsť pri vyšetrovaní a nemajú právo zúčastňovať sa na vyšetrovacích činnostiach, okrem prípadov, keď ich o to požiada poradca pre etiku, predseda Výboru pre audit alebo vyšetrovatelia.',
          },
          {
            kind: 'p',
            text: 'Chráneným oznámením sa primerane zaoberá poradca pre etiku, prípadne predseda Výboru pre audit.',
          },
        ],
      },
      {
        id: 'commencement',
        num: '03',
        title: 'Dátum začatia',
        blocks: [
          {
            kind: 'p',
            text: 'Táto Politika nadobudla účinnosť 8. mája 2006, keď ju prijalo predstavenstvo Spoločnosti.',
          },
        ],
      },
      {
        id: 'eligibility',
        num: '04',
        title: 'Oprávnenosť',
        blocks: [
          {
            kind: 'p',
            text: 'Všetci riaditelia a zamestnanci Spoločnosti sú oprávnení podávať chránené oznámenia podľa tejto Politiky. Chránené oznámenia sa môžu týkať záležitostí Spoločnosti alebo akejkoľvek inej spoločnosti Tata.',
          },
        ],
      },
      {
        id: 'disqualifications',
        num: '05',
        title: 'Diskvalifikácie',
        blocks: [
          {
            kind: 'p',
            text: 'Skutočným oznamovateľom sa poskytne úplná ochrana pred nespravodlivým zaobchádzaním, ako je uvedené v tejto Politike. Akékoľvek zneužitie tejto ochrany je dôvodom na disciplinárne konanie.',
          },
          {
            kind: 'p',
            text: 'Ochrana podľa tejto Politiky neznamená ochranu pred disciplinárnym konaním, ktoré vyplýva z nepravdivých alebo falošných obvinení podaných oznamovateľom s vedomím, že sú nepravdivé alebo falošné, alebo s nekalým úmyslom.',
          },
          {
            kind: 'p',
            text: 'Oznamovatelia, ktorí podajú tri alebo viac chránených oznámení, o ktorých sa následne zistí, že boli podané s nekalým úmyslom, ľahkovážne, neopodstatnene, zlomyselne alebo inak ako v dobrej viere, budú vylúčení z podávania ďalších chránených oznámení podľa tejto Politiky. Vo vzťahu k takýmto oznamovateľom si Spoločnosť a Výbor pre audit vyhradzujú právo prijať alebo odporučiť primerané disciplinárne opatrenie.',
          },
        ],
      },
      {
        id: 'definitions',
        num: '06',
        title: 'Vymedzenie pojmov',
        blocks: [
          {
            kind: 'p',
            text: 'Pojmy s veľkým začiatočným písmenom, ktoré tu nie sú definované, majú význam podľa Kódexu.',
          },
          {
            kind: 'defs',
            items: [
              {
                term: 'Výbor pre audit',
                text: 'Výbor pre audit zriadený predstavenstvom Spoločnosti v súlade s článkom 177 zákona o spoločnostiach z roku 2013.',
              },
              {
                term: 'Zamestnanec',
                text: 'Každý zamestnanec Spoločnosti bez ohľadu na kategóriu.',
              },
              {
                term: 'Riaditeľ',
                text: 'Člen predstavenstva Spoločnosti.',
              },
              {
                term: 'Kódex',
                text: 'Kódex správania spoločnosti Tata.',
              },
              {
                term: 'Vyšetrovatelia',
                text: 'Osoby, ktoré poveril, vymenoval, konzultoval alebo oslovil poradca pre etiku alebo predseda Výboru pre audit, vrátane audítorov Spoločnosti a polície.',
              },
              {
                term: 'Chránené oznámenie',
                text: 'Akákoľvek komunikácia uskutočnená v dobrej viere, ktorá odhaľuje alebo preukazuje informácie, ktoré môžu svedčiť o neetickej alebo nevhodnej činnosti.',
              },
              {
                term: 'Subjekt',
                text: 'Osoba, voči ktorej alebo v súvislosti s ktorou bolo podané chránené oznámenie, alebo vo vzťahu ku ktorej sa počas vyšetrovania zhromaždili dôkazy.',
              },
              {
                term: 'Oznamovateľ',
                text: 'Zamestnanec, ktorý podáva chránené oznámenie podľa tejto Politiky.',
              },
            ],
          },
        ],
      },
      {
        id: 'procedure',
        num: '07',
        title: 'Postup',
        blocks: [
          {
            kind: 'p',
            text: 'Všetky chránené oznámenia v akejkoľvek veci sa adresujú poradcovi pre etiku Spoločnosti alebo predsedovi Výboru pre audit príslušnej obchodnej jednotky na prešetrenie. Možno ich poslať aj na whistle.blower@tataautocomp.com.',
          },
          {
            kind: 'p',
            text: 'Všetky chránené oznámenia vo veciach financií alebo účtovníctva sa adresujú predsedovi Výboru pre audit Spoločnosti na prešetrenie.',
          },
          {
            kind: 'p',
            text: `Poradcu pre etiku a predsedníčku Výboru pre audit kontaktujte takto. Pani Varsha Purandare, predsedníčka Výboru pre audit, ${address}, varsha0712@outlook.com. Sudipta Marjit, hlavný etický poradca, ${address}, Sudipta.marjit@tataautocomp.com.`,
          },
          {
            kind: 'list',
            items: [
              'Ak chránené oznámenie dostane ktorýkoľvek vedúci pracovník Spoločnosti iný ako predseda Výboru pre audit alebo poradca pre etiku, postúpi ho poradcovi pre etiku Spoločnosti a/alebo predsedovi Výboru pre audit. Je potrebné dbať na utajenie totožnosti oznamovateľa.',
              'Chránené oznámenia sa prednostne podávajú písomne, aby boli nastolené otázky jasne zrozumiteľné. Majú byť napísané na stroji alebo čitateľným rukopisom v angličtine, hindčine alebo v regionálnom jazyku miesta zamestnania oznamovateľa.',
              'Chránené oznámenie sa zasiela so sprievodným listom, na ktorom je uvedená totožnosť oznamovateľa. Predseda Výboru pre audit alebo poradca pre etiku sprievodný list oddelí a vyšetrovateľom postúpi len chránené oznámenie.',
              'Chránené oznámenia majú byť vecné, nie špekulatívne ani formulované ako hotový záver. Majú obsahovať čo najviac konkrétnych informácií, aby bolo možné posúdiť povahu a rozsah obavy a naliehavosť predbežného vyšetrovania.',
              'Oznamovateľ musí uviesť svoju totožnosť v sprievodnom liste. Anonymné oznámenia sa nemusia prijať, pretože oznamovateľa by nebolo možné vypočuť.',
            ],
          },
        ],
      },
      {
        id: 'investigation',
        num: '08',
        title: 'Vyšetrovanie',
        blocks: [
          {
            kind: 'p',
            text: 'Všetky chránené oznámenia podané podľa tejto Politiky dôkladne prešetrí poradca pre etiku alebo predseda Výboru pre audit Spoločnosti. Vyšetrovanie vedú alebo nad ním dohliadajú na základe poverenia Výboru pre audit.',
          },
          {
            kind: 'p',
            text: 'Poradca pre etiku alebo predseda Výboru pre audit môže podľa vlastného uváženia zapojiť do vyšetrovania vyšetrovateľov.',
          },
          {
            kind: 'p',
            text: 'Rozhodnutie začať vyšetrovanie samo osebe nie je obvinenie. Ide o neutrálne zisťovanie skutkového stavu. Výsledok nemusí potvrdiť záver oznamovateľa, že došlo k nevhodnému alebo neetickému konaniu.',
          },
          {
            kind: 'p',
            text: 'Totožnosť subjektu sa utají v rozsahu, v akom to dovoľujú oprávnené potreby zákona a vyšetrovania. Subjekt sa spravidla oboznámi s obvineniami na začiatku formálneho vyšetrovania a počas vyšetrovania môže predložiť svoje vyjadrenie.',
          },
          {
            kind: 'p',
            text: 'Subjekt je povinný spolupracovať s poradcom pre etiku, predsedom Výboru pre audit alebo s vyšetrovateľmi v rozsahu, v akom táto spolupráca neohrozí ochranu pred sebaobviňovaním podľa platných právnych predpisov.',
          },
          {
            kind: 'p',
            text: 'Subjekt má právo konzultovať s osobou alebo osobami podľa vlastného výberu, inými ako poradca pre etiku, vyšetrovatelia, členovia Výboru pre audit alebo oznamovateľ. Subjekt si môže kedykoľvek na vlastné náklady zvoliť právneho zástupcu, ktorý ho bude zastupovať vo vyšetrovacom konaní.',
          },
          {
            kind: 'p',
            text: 'Subjekt nesmie zasahovať do vyšetrovania. Dôkazy sa nesmú zadržiavať, ničiť ani s nimi manipulovať a svedkovia nesmú byť ovplyvňovaní, navádzaní, ohrozovaní ani zastrašovaní.',
          },
          {
            kind: 'p',
            text: 'Pokiaľ na to nie sú závažné dôvody, subjekt dostane príležitosť vyjadriť sa k podstatným zisteniam v správe o vyšetrovaní. Obvinenie z protiprávneho konania voči subjektu nie je opodstatnené, pokiaľ nie je podložené riadnymi dôkazmi.',
          },
          {
            kind: 'p',
            text: 'Subjekt má právo byť informovaný o výsledku vyšetrovania. Ak sa obvinenia nepotvrdia, so subjektom sa konzultuje, či by zverejnenie výsledkov vyšetrovania bolo v najlepšom záujme subjektu a Spoločnosti.',
          },
          {
            kind: 'p',
            text: 'Vyšetrovanie sa spravidla ukončí do 45 dní od prijatia chráneného oznámenia.',
          },
        ],
      },
      {
        id: 'protection',
        num: '09',
        title: 'Ochrana',
        blocks: [
          {
            kind: 'p',
            text: 'S oznamovateľom sa nebude zaobchádzať nespravodlivo preto, že podľa tejto Politiky podal chránené oznámenie. Spoločnosť odsudzuje akúkoľvek diskrimináciu, obťažovanie, viktimizáciu alebo inú nekalú zamestnaneckú praktiku voči oznamovateľom.',
          },
          {
            kind: 'p',
            text: 'Oznamovateľom sa preto poskytne úplná ochrana pred nekalými praktikami, ako sú odvetné opatrenia, hrozba alebo zastrašovanie prepustením či pozastavením výkonu práce, disciplinárne konanie, preloženie, degradácia, odmietnutie povýšenia a podobne, vrátane priameho alebo nepriameho využitia právomoci na zabránenie tomu, aby oznamovateľ naďalej vykonával svoje povinnosti a funkcie, a to aj podaním ďalšieho chráneného oznámenia.',
          },
          {
            kind: 'p',
            text: 'Priamy prístup k predsedovi Výboru pre audit sa poskytne v primeraných a výnimočných prípadoch. Spoločnosť podnikne kroky na zmiernenie ťažkostí, ktoré môžu oznamovateľovi vzniknúť v dôsledku podania chráneného oznámenia. Ak má oznamovateľ svedčiť v trestnom alebo disciplinárnom konaní, Spoločnosť zabezpečí, aby dostal poučenie o postupe.',
          },
          {
            kind: 'p',
            text: 'Porušenie uvedených pravidiel môže oznamovateľ oznámiť predsedovi Výboru pre audit, ktorý vec prešetrí a odporučí vedeniu vhodné opatrenie.',
          },
          {
            kind: 'p',
            text: 'Totožnosť oznamovateľa sa zachová v tajnosti v rozsahu, v akom je to možné a právne dovolené. Oznamovatelia berú na vedomie, že ich totožnosť sa môže stať známou z dôvodov mimo kontroly poradcu pre etiku alebo predsedu Výboru pre audit, napríklad počas vyšetrovania vedeného vyšetrovateľmi.',
          },
          {
            kind: 'p',
            text: 'Každý iný zamestnanec, ktorý pri vyšetrovaní pomáha, je chránený v rovnakom rozsahu ako oznamovateľ.',
          },
        ],
      },
      {
        id: 'investigators',
        num: '10',
        title: 'Vyšetrovatelia',
        blocks: [
          {
            kind: 'p',
            text: 'Vyšetrovatelia vedú proces zisťovania a analýzy skutkového stavu. Svoje právomoci a prístupové práva odvodzujú od poradcu pre etiku alebo Výboru pre audit, keď konajú v rámci predmetu a rozsahu vyšetrovania.',
          },
          {
            kind: 'p',
            text: 'Na doplnenie vyšetrovania možno podľa potreby využiť technické a iné zdroje. Všetci vyšetrovatelia musia byť nezávislí a nestranní, v skutočnosti aj navonok. Vyšetrovatelia sú povinní postupovať spravodlivo, objektívne, dôkladne a eticky a dodržiavať právne a profesijné normy.',
          },
          {
            kind: 'p',
            text: 'Vyšetrovanie sa začne až po predbežnom posúdení, ktorým sa zistí, že:',
          },
          {
            kind: 'list',
            items: [
              'údajný čin predstavuje nevhodnú alebo neetickú činnosť alebo správanie a',
              'obvinenie je podložené informáciami dostatočne konkrétnymi na prešetrenie, alebo tento štandard nespĺňa. Veci, ktoré ho nespĺňajú, môžu byť hodné preskúmania vedením, nemajú sa však viesť ako vyšetrovanie nevhodnej alebo neetickej činnosti.',
            ],
          },
        ],
      },
      {
        id: 'decision',
        num: '11',
        title: 'Rozhodnutie',
        blocks: [
          {
            kind: 'p',
            text: 'Ak vyšetrovanie vedie poradcu pre etiku alebo predsedu Výboru pre audit k záveru, že došlo k nevhodnému alebo neetickému konaniu, odporučia vedeniu Spoločnosti disciplinárne alebo nápravné opatrenie, ktoré považujú za vhodné. Disciplinárne alebo nápravné opatrenie voči subjektu sa riadi platnými postupmi pre správanie zamestnancov a disciplinárnymi postupmi.',
          },
        ],
      },
      {
        id: 'reporting',
        num: '12',
        title: 'Podávanie správ',
        blocks: [
          {
            kind: 'p',
            text: 'Poradca pre etiku pravidelne predkladá Výboru pre audit správu o všetkých chránených oznámeniach, ktoré mu boli postúpené od poslednej správy, spolu s prípadnými výsledkami vyšetrovania.',
          },
        ],
      },
      {
        id: 'retention',
        num: '13',
        title: 'Uchovávanie dokumentov',
        blocks: [
          {
            kind: 'p',
            text: 'Všetky písomné alebo zdokumentované chránené oznámenia spolu s výsledkami súvisiaceho vyšetrovania uchováva Spoločnosť najmenej sedem rokov.',
          },
        ],
      },
      {
        id: 'amendment',
        num: '14',
        title: 'Právomoc meniť politiku',
        blocks: [
          {
            kind: 'p',
            text: 'Spoločnosť si vyhradzuje právo kedykoľvek úplne alebo čiastočne zmeniť alebo doplniť túto Politiku bez uvedenia dôvodu.',
          },
        ],
      },
    ],
  },
};
